import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useIntl } from "react-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { platformApi } from "@/app/platform";
import { Button } from "@/components/Button";
import { ButtonBase } from "@/components/ButtonBase/ButtonBase";
import { IconFlightService } from "@/components/Icons";
import { CaretRightIcon } from "@/components/Icons/rtlReadyIcons";
import { Image } from "@/components/Image/Image";
import { CabinClass, TripType } from "@/features/flights/types/types";
import type { HotelSearchFormValues } from "@/features/hotels/utils/hotelsValidation";
import type { FindUserReservationExternalHotelReservation } from "@/types/codegen";

type Props = {
  reservation: FindUserReservationExternalHotelReservation;
  destination?: HotelSearchFormValues["destination"];
};

export const HotelBookingUpsellCards = ({
  reservation,
  destination,
}: Props) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const handleFlightSuggestionClick = () => {
    if (!destination) return;

    const travellers = reservation.travelers;
    const { adult, child, infant } = travellers.reduce(
      (acc, traveller) => {
        return { ...acc, [traveller.type]: acc[traveller.type] + 1 };
      },
      { adult: 0, child: 0, infant: 0 },
    );
    const formData = {
      adult,
      child,
      infantWithoutSeat: infant,
      cabinClass: CabinClass.ANY,
      tripType: TripType.ROUND_TRIP,
      dates: {
        departure: dayjs(reservation.serviceDates.startDate).toDate(),
        return: dayjs(reservation.serviceDates.endDate).toDate(),
      },
      to: destination,
      from: null,
    };
    navigate({
      to: "/flights/search",
      state: (prev) => ({
        ...prev,
        flightSearchPrefill: formData,
      }),
    });
  };

  return (
    <div className="space-y-4">
      {useFeatureIsOn("e-sim-navigation-link") ? (
        <div className="section-card space-y-4">
          <div className="flex justify-between gap-2">
            <div className="space-y-0.5">
              <div className="text-lg font-medium text-black">
                {intl.formatMessage({
                  defaultMessage: "Skip the roaming fees",
                  description: "Title of eSim upsell card",
                })}
              </div>
              <div className="text-sm text-text-tertiary">
                {destination
                  ? intl.formatMessage(
                      {
                        defaultMessage:
                          "Stay online in {cityName} with a digital eSIM. No physical cards, no unexpected bills.",
                        description:
                          "Subtitle of eSim upsell card with known destination",
                      },
                      {
                        cityName: destination.name,
                      },
                    )
                  : intl.formatMessage({
                      defaultMessage:
                        "Stay online with a digital eSIM. No physical cards, no unexpected bills.",
                      description:
                        "Subtitle of eSim upsell card with unknown destination",
                    })}
              </div>
            </div>
            <Image src="/eSimUpsell.png" className="size-20 shrink-0" />
          </div>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => platformApi.navigateToMiniApp("3488020210984303")}
          >
            {intl.formatMessage({
              defaultMessage: "View eSim Plans",
              description:
                "Button on hotel booking success page which redirects user to eSim miniapp",
            })}
          </Button>
        </div>
      ) : null}
      <div className="section-card">
        {destination ? (
          <ButtonBase
            onClick={handleFlightSuggestionClick}
            className="flex w-full items-center gap-2 rounded-2xl bg-white"
          >
            <IconFlightService className="size-10 shrink-0" />
            <div className="space-y-0.5 text-start">
              <div className="text-base font-medium text-black">
                {intl.formatMessage(
                  {
                    defaultMessage: "Book a flight to {city}",
                    description: "Flight suggestion essential card title",
                  },
                  {
                    city: destination?.name,
                  },
                )}
              </div>
              <div className="text-sm text-text-placeholder">
                {intl.formatMessage(
                  {
                    defaultMessage:
                      "We found flights to {city} that match your hotel dates",
                    description: "Flight suggestion essential card subtitle",
                  },
                  {
                    city: destination?.name,
                  },
                )}
              </div>
            </div>
            <CaretRightIcon
              weight="bold"
              className="ms-auto size-4 shrink-0 text-stone"
            />
          </ButtonBase>
        ) : null}
      </div>
    </div>
  );
};
